/*
 *   File : index.js
 *   Author URI : https://evoqueinnovativelab.com
 *   Description : Index file for Orders container;
 *   Integrations :  12-08-2022
 *   Version : v1.0
 *   Created :  12-09-2022
*/
/*import packages*/
import { useState, useEffect, useRef, Fragment } from 'react';
import devconfig from '../../config/config';
import { EmptyNote, Toast } from '../../components/note';
import { DataTable } from '../../components/dataFormating';
import { Loader } from '../../components/loader';
import APIPost from '../../services/postApi';
import { useNavigate } from 'react-router-dom';
import SelectBox from '../../components/dropdown/selectBox';
import APIGet from '../../services/getApi';
import { DateRangePicker } from '../../components/input/index';
import { DownloadBtn } from '../../components/cta/index';

/* "All" sentinel for the Order Type filter — value=null means no filter.
 * The remaining options come from /order/folio-filter (order_type_data). */
const ALL_ORDER_TYPE_OPTION = { value: null, label: "All" };

const ExternalTransactions = (props) => {
    const navigate = useNavigate();
    const [orderData, setOrderData] = useState([]);
    const [pageNum, setPageNum] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const [loader, setLoader] = useState(true);
    const [downloadLoader, setDownloadLoader] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [totalRecords, setTotalRecords] = useState(null);
    /* Total amount for the currently applied filters, across all pages.
     * Provided by the backend as `total_amount` alongside `total_records` —
     * not computed from the current page's `data`. */
    const [totalAmount, setTotalAmount] = useState(null);
    /* Rows currently checked in the table. react-data-table hands back its
     * selection state on every change; we keep just the selectedRows array so
     * the footer total can be derived from it. */
    const [selectedRows, setSelectedRows] = useState([]);

    /* Filters: folio dropdown + transaction date range.
     * Folio options come from /order/external-transaction-folio-filter and
     * always include an "All" entry with id=null which means "no filter". */
    const [dates, setDates] = useState({ start: null, end: null });
    const ALL_FOLIO_OPTION = { value: null, label: "All" };
    const [folioOptions, setFolioOptions] = useState([ALL_FOLIO_OPTION]);
    const [selectedFolio, setSelectedFolio] = useState(ALL_FOLIO_OPTION);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [selectedSource, setSelectedSource] = useState(null);
  
    const [orderTypeOptions, setOrderTypeOptions] = useState([ALL_ORDER_TYPE_OPTION]);
    const [selectedOrderType, setSelectedOrderType] = useState(ALL_ORDER_TYPE_OPTION);

    useEffect(() => {
        _getFolioFilter();
        //eslint-disable-next-line
    }, [props.id]);

    useEffect(() => {
        getExternalTransactions();
        //eslint-disable-next-line
    }, [perPage, pageNum, dates, selectedFolio?.value, selectedSource?.value, selectedOrderType?.value]);

    useEffect(() => {
        if (error !== null) {
            const t = setTimeout(() => setError(null), 3000);
            return () => clearTimeout(t);
        }
    }, [error]);

    useEffect(() => {
        if (success !== null) {
            const t = setTimeout(() => setSuccess(null), 3000);
            return () => clearTimeout(t);
        }
    }, [success]);

    const _getSourceValue = (item) => {
        if (item?.id === null) return null;
        if (item?.name?.toLowerCase() === "external") return true;
        if (item?.name?.toLowerCase() === "internal") return false;
        return item?.id;
    };

    /* Pulls the folio list for this customer. Maps {id,label} → {value,label}
     * because SelectBox expects `value`. */
    const _getFolioFilter = () => {
        if (!props.id) return;
        const url = devconfig.admin + '/order/folio-filter';
        const data = JSON.stringify({ customer_id: Number(props.id) });
        APIPost(url, data).then((response) => {
            if (response?.status_code === 200) {
                const mapped = Array.isArray(response.data?.folio) ? response.data.folio.map((f) => ({ value: f.id, label: f.label })) : [];
                /* Always prepend "All" so the user can clear the filter even
                 * if the backend doesn't include it. Strip any duplicate "All"
                 * the backend may already have sent. */
                const withoutAll = mapped.filter((o) => o.value !== null && o.label !== "All");
                setFolioOptions([ALL_FOLIO_OPTION, ...withoutAll]);

                const mappedOrderType = Array.isArray(response.data?.order_type) ? response.data.order_type.map((item) => ({
                    value: _getSourceValue(item),
                    label: item.name,
                })) : [];
                setSourceOptions(mappedOrderType);
                setSelectedSource(mappedOrderType.find((item) => item.label === "External") || mappedOrderType[0] || null);

         
                const mappedOrderTypeData = Array.isArray(response.data?.order_type_data) ? response.data.order_type_data.map((item) => ({
                    value: item.id ?? item.value,
                    label: item.name ?? item.label,
                })) : [];
                /* order_type_data already includes an "All" — strip it and use
                 * our own null-valued "All" so value=null stays "no filter". */
                const withoutAllTypes = mappedOrderTypeData.filter((o) => o.value !== null && String(o.label).toLowerCase() !== "all");
                setOrderTypeOptions([ALL_ORDER_TYPE_OPTION, ...withoutAllTypes]);
                setSelectedOrderType(ALL_ORDER_TYPE_OPTION);
            } else {
                setFolioOptions([ALL_FOLIO_OPTION]);
                setSourceOptions([]);
                setSelectedSource(null);
                setOrderTypeOptions([ALL_ORDER_TYPE_OPTION]);
                setSelectedOrderType(ALL_ORDER_TYPE_OPTION);
            }
        }).catch(() => {
            setFolioOptions([ALL_FOLIO_OPTION]);
            setSourceOptions([]);
            setSelectedSource(null);
            setOrderTypeOptions([ALL_ORDER_TYPE_OPTION]);
            setSelectedOrderType(ALL_ORDER_TYPE_OPTION);
        });
    };

    const columns = [
        {
            name: 'Traded on',
            selector: row => row.traded_on,
            sortable: false,
            left: true,
            width: '200px',
            // sortFunction: (a, b) => customDateSort(a, b, 'order_time',),
            cell: row => <div className="col-lg-12" >
                {row.traded_on} {row.traded_at}
            </div>
        },
        {
            name: 'Fund name',
            cell: (row) => row.fund_name ? row.fund_name : '--',
            sortable: true,
            left: true,
            width: "450px",
            defaultSortAsc: false,
        },
        {
            name: 'Type',
            selector: row => row.order_type,
            sortable: true,
            left: true,
            width: "150px",
            cell: (row) => <div className="col-lg-12">
                {row.order_type}
            </div>
        },
        {
            name: 'Amount (₹)',
            selector: row => row.amount ? parseFloat(row.amount) : 0,
            // selector: (row) => row.amount ? "₹" + parseFloat(row.amount.split("₹")[1]).toFixed(2) : '--',
            sortable: true,
            right: true,
            minWidth: '130px',
            cell: row => <div>
                {
                    row.amount ?
                        row.amount.toFixed(2)
                        :
                        0
                }
            </div>

        },
        {
            name: 'Units',
            selector: row => row.units,
            // selector: (row) => row.amount ? "₹" + parseFloat(row.amount.split("₹")[1]).toFixed(2) : '--',
            sortable: true,
            left: true,
            width: '130px',
            cell: row => <div>
                {row.units ? row.units : "-"}
            </div>

        },
        {
            name: 'ISIN',
            selector: row => row.isin,
            sortable: true,
            left: true,
            width: '150px',
            cell: row => <div className="col-lg-12">
                {row.isin ? row.isin : "-"}
            </div>
        },
        {
            name: 'Created on',
            selector: row => row.created,
            sortable: false,
            left: true,
            width: '150px',
            // sortFunction: (a, b) => customDateSort(a, b, 'order_time',),
            cell: row => <div className="col-lg-12" >
                {row.created}
            </div>
        },
        {
            name: 'Folio No.',
            selector: row => row.folio_number,
            sortable: true,
            left: true,
            width: '180px',
            cell: (row) => <div className="col-lg-12">
                {row.folio_number ? row.folio_number : "-"}
            </div>
        },
        {
            name: 'Transaction ID',
            selector: row => row.rta_order_reference,
            // selector: (row) => row.amount ? "₹" + parseFloat(row.amount.split("₹")[1]).toFixed(2) : '--',
            sortable: true,
            left: true,
            width: '180px',
            cell: row => <div>
                {row.rta_order_reference ? row.rta_order_reference : "-"}
            </div>

        },
        {
            name: 'RTA Product Code',
            selector: row => row.rta_product_code,
            sortable: true,
            left: true,
            width: '180px',
            cell: row => <div>
                {row.rta_product_code ? row.rta_product_code : "-"}
            </div>
        },
        {
            name: 'Investment Option',
            selector: row => row.rta_investment_option,
            sortable: true,
            left: true,
            width: '220px',
            cell: row => <div>
                {row.rta_investment_option ? row.rta_investment_option : "-"}
            </div>
        },


    ]

    const _handlePageChange = (page_num) => {
        setPageNum(page_num);
    };

    const _handlePerPage = (per_page) => {
        setPerPage(per_page);
    };

    /* Total amount across the currently selected rows. Uses the same numeric
     * `amount` field as the Amount column; recomputed on every render so it
     * stays in sync whenever the selection changes. ₹0.00 when nothing is
     * selected. */
    const selectedTotalAmount = selectedRows.reduce(
        (sum, r) => sum + (r.amount ? parseFloat(r.amount) : 0),
        0
    );

    /*--------- order list -----------*/
    const getExternalTransactions = () => {
        if (!selectedSource) return;
        setLoader(true);
        let url = devconfig.admin + '/order/external-transaction';

        let data = JSON.stringify({
            user_id: Number(props.id),
            page_num: pageNum,
            page_size: perPage,
            /* Send the label (the actual folio number string). value=null means
             * "All" was selected, in which case we omit the filter. */
            folio_number: selectedFolio?.value ? selectedFolio.label : null,
            start_date: dates.start,
            end_date: dates.end,
            is_external: selectedSource?.value,
            /* value=null means "All" → no order-type filter */
            order_type: selectedOrderType?.value ?? null,
        });
        APIPost(url, data).then((response) => {
            if (response.status_code === 200) {
                setOrderData(response.data.data ?? response.data);
                setTotalRecords(response.data.total_records)
                setTotalAmount(response.data.total_amount ?? null);
            } else {
                setOrderData([]);
            }
            setLoader(false);
        });
    }

    /*--------- download external transactions report -----------*/
    /* Mirrors the list filters: folio + date range. folio_number is sent as
     * the label (the actual folio string) because value=null represents "All". */
    const _downloadExternalTransactions = () => {
        setDownloadLoader(true);
        const url = devconfig.admin + '/order/external-download';
        const data = JSON.stringify({
            user_id: Number(props.id),
            folio_number: selectedFolio?.value ? selectedFolio.label : null,
            start_date: dates.start,
            end_date: dates.end,
            is_external: selectedSource?.value,
            order_type: selectedOrderType?.value ?? null,
        });
        APIPost(url, data).then((response) => {
            setDownloadLoader(false);
            if (response?.status_code === 200 && response?.status === true) {
                if (response.data?.url) {
                    const a = document.createElement("a");
                    a.href = response.data.url;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setSuccess("Report downloaded successfully.");
                } else {
                    setError("Download URL is missing in the response.");
                }
            } else {
                setError(response?.message || "Failed to download the report.");
            }
        }).catch(() => {
            setDownloadLoader(false);
            setError("Failed to download the report.");
        });
    }

    return (
        <Fragment>
            <div className="row pe-0">
                <div className="col-lg-12 pe-0">
                    <div className="card e-dashboard-card border-0 py-1 px-0">
                        <div className="row pt-2 align-items-center">
                            <div className="col-lg-12">
                                <h6 className="e-section-title mb-0 px-3">All Transactions</h6>
                            </div>
                        </div>

                        {/*--------------- Filters: date range + folio ---------------*/}
                        <div className="e-filter-wraper e-sort mt-1">
                            <div className="row align-items-end">
                                <div className="col-lg-3 pe-1 col-4">
                                    <label className="mb-1">Transactions from - till</label>
                                    <DateRangePicker
                                        onDatesChange={(start, end) => {
                                            setPageNum(1);
                                            setPerPage(10);
                                            setDates({ start: start, end: end });
                                        }}
                                        start={dates.start}
                                        end={dates.end}
                                    />
                                </div>
                                <div className="col-lg-3 px-1 col-3">
                                    <label className="mb-1">Folio number</label>
                                    <SelectBox
                                        value={selectedFolio}
                                        minHeight="39px"
                                        options={folioOptions}
                                        placeholder="Select folio"
                                        isSearchable={true}
                                        selectchange={(opt) => {
                                            setPageNum(1);
                                            setPerPage(10);
                                            setSelectedFolio(opt || ALL_FOLIO_OPTION);
                                        }}
                                    />
                                </div>
                                <div className="col-lg-3 px-1 col-3">
                                    <label className="mb-1">Order source</label>
                                    <SelectBox
                                        value={selectedSource}
                                        minHeight="39px"
                                        options={sourceOptions}
                                        placeholder="Select source"
                                        isSearchable={false}
                                        selectchange={(opt) => {
                                            setPageNum(1);
                                            setPerPage(10);
                                            setSelectedSource(opt || sourceOptions[0] || null);
                                        }}
                                    />
                                </div>
                                <div className="col-lg-2 px-1 col-3">
                                    <label className="mb-1">Order type</label>
                                    <SelectBox
                                        value={selectedOrderType}
                                        minHeight="39px"
                                        options={orderTypeOptions}
                                        placeholder="Select type"
                                        isSearchable={false}
                                        selectchange={(opt) => {
                                            setPageNum(1);
                                            setPerPage(10);
                                            setSelectedOrderType(opt || ALL_ORDER_TYPE_OPTION);
                                        }}
                                    />
                                </div>
                                {/*--------------- Download button beside the dropdowns ---------------*/}
                                <div
                                    className="col-lg-1 ps-1 text-end"
                                    style={{
                                        opacity: orderData.length === 0 ? 0.4 : 1,
                                        pointerEvents: orderData.length === 0 ? "none" : "auto",
                                    }}
                                    title={orderData.length === 0 ? "No transactions to download" : ""}
                                >
                                    <DownloadBtn
                                        check={["check"]}
                                        loader={downloadLoader}
                                        onClick={() => {
                                            if (downloadLoader || orderData.length === 0) return;
                                            _downloadExternalTransactions();
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/*--------------- Order table -----------*/}
                        {
                            loader ?
                                <Loader />
                                :
                                (!orderData.length) ?
                                    <EmptyNote note="There are no transactions to show" />
                                    :
                                    <div className="col-lg-12 px-0 e-no-row-pointer" style={{ position: "relative" }}>
                                        <DataTable
                                            /* Remount on filter change so the table's internal page resets
                                             * to paginationDefaultPage — otherwise the footer stays on a stale
                                             * page after the result set shrinks. */
                                            key={`ext-tx-${dates.start || ''}-${dates.end || ''}-${selectedFolio?.value ?? 'all'}-${selectedSource?.value}-${selectedOrderType?.value ?? 'all'}`}
                                            pagination={true}
                                            columns={columns}
                                            data={orderData}
                                            perPage={perPage}
                                            paginationServer={true}
                                            paginationDefaultPage={pageNum}
                                            selectableRows={true}
                                            paginationTotalRows={totalRecords}
                                            onChangeRowsPerPage={_handlePerPage}
                                            onChangePage={_handlePageChange}
                                            /* Capture the current selection so the footer total stays in
                                             * sync. react-data-table passes { selectedRows, ... }. */
                                            selectRows={(row) => setSelectedRows(row?.selectedRows || [])} />

                                        {/* Total for the currently applied filters (backend-provided, across
                                            all pages), plus the selected-rows total when a selection is
                                            active — both sit on the left of the pagination footer
                                            ("Rows per page" stays on the right). */}
                                        <div className="e-selected-total"
                                            style={{
                                                position: "absolute",
                                                left: "8px",
                                                bottom: 0,
                                                height: "56px",
                                                display: "flex",
                                                alignItems: "center",
                                                fontWeight: 600,
                                            }}>
                                            Total:&nbsp;{totalAmount == null ? '—' : '₹' + totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            {
                                                selectedRows.length > 0 &&
                                                <span style={{ marginLeft: "16px" }}>
                                                    Selected:&nbsp;₹{selectedTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            }
                                        </div>
                                    </div>
                        }
                    </div>
                </div>
            </div>
            {/* error toast */}
            {
                error !== null ?
                    <Toast
                        data={error}
                        type="eror"
                        id="1"
                    />
                    :
                    null
            }
            {/* success toast */}
            {
                success !== null ?
                    <Toast
                        data={success}
                        type="success"
                        id="2"
                    />
                    :
                    null
            }
        </Fragment>
    )
}

export default ExternalTransactions
